import { motion } from "framer-motion";
import { ArrowRight, TrendingUp, TrendingDown } from "lucide-react";

/**
 * PainPoints — "Is this your problem?"
 * Industry-average vs Clinic OS side-by-side stats.
 * Lets the visitor *see* themselves and *see* the lift, in one glance.
 *
 * Props
 *  - heading, sub, eyebrow         — copy
 *  - stats: Array<{ label, industry, ours, direction, sourceTag, accent }>
 *  - variant: "default" | "inline" (inline = no section padding, for embedding)
 *  - className                     — extra wrapper classes
 */

export const DEFAULT_STATS = [
  {
    label: "Inbound patient calls answered",
    industry: "62%",
    ours: "97%",
    direction: "up",
    sourceTag: "Industry avg → Clinic OS pilots",
    accent: "rose",
  },
  {
    label: "Average no-show rate",
    industry: "20%",
    ours: "8%",
    direction: "down",
    sourceTag: "Industry avg → Clinic OS pilots",
    accent: "amber",
  },
  {
    label: "First reply to a new lead",
    industry: "~8 hrs",
    ours: "< 60 sec",
    direction: "down",
    sourceTag: "Industry avg → AI Receptionist",
    accent: "violet",
  },
  {
    label: "Front-desk admin hrs / week",
    industry: "11 hrs",
    ours: "3 hrs",
    direction: "down",
    sourceTag: "Clinic OS clinic average",
    accent: "blue",
  },
];

export const GROWTH_STATS = [
  {
    label: "Inbound calls answered after-hours",
    industry: "31%",
    ours: "100%",
    direction: "up",
    sourceTag: "AI Receptionist",
    accent: "rose",
  },
  {
    label: "Lead → booking conversion",
    industry: "9%",
    ours: "24%",
    direction: "up",
    sourceTag: "Clinic OS pilots",
    accent: "emerald",
  },
  {
    label: "Cost per booked patient (paid ads)",
    industry: "$87",
    ours: "$19",
    direction: "down",
    sourceTag: "Reactivation flow",
    accent: "violet",
  },
];

export const COMMS_STATS = [
  {
    label: "Missed calls converted to bookings",
    industry: "0%",
    ours: "41%",
    direction: "up",
    sourceTag: "Auto SMS-back",
    accent: "rose",
  },
  {
    label: "Avg first reply across channels",
    industry: "~8 hrs",
    ours: "< 60 sec",
    direction: "down",
    sourceTag: "Unified Inbox + AI",
    accent: "violet",
  },
  {
    label: "Review reply rate (Google + Facebook)",
    industry: "23%",
    ours: "98%",
    direction: "up",
    sourceTag: "Review AI",
    accent: "amber",
  },
];

export const DATA_STATS = [
  {
    label: "Time to see clinic-wide revenue",
    industry: "weekly",
    ours: "live",
    direction: "down",
    sourceTag: "Clinic Pulse",
    accent: "blue",
  },
  {
    label: "EMR → marketing data sync",
    industry: "manual",
    ours: "2-way",
    direction: "up",
    sourceTag: "Native EMR bridge",
    accent: "emerald",
  },
  {
    label: "Recall cycle conversion",
    industry: "30%",
    ours: "55%",
    direction: "up",
    sourceTag: "Reactivation engine",
    accent: "amber",
  },
];

const ACCENTS = {
  rose:    { ring: "border-rose-500/30",    chip: "text-rose-600 dark:text-rose-300",   glow: "bg-rose-500/15" },
  amber:   { ring: "border-amber-500/30",   chip: "text-amber-700 dark:text-amber-300", glow: "bg-amber-500/15" },
  violet:  { ring: "border-violet-500/30",  chip: "text-violet-600 dark:text-violet-300", glow: "bg-violet-500/15" },
  blue:    { ring: "border-blue-500/30",    chip: "text-blue-600 dark:text-blue-300",   glow: "bg-blue-500/15" },
  emerald: { ring: "border-emerald-500/30", chip: "text-emerald-600 dark:text-emerald-300", glow: "bg-emerald-500/15" },
};

const StatCard = ({ s, i }) => {
  const tone = ACCENTS[s.accent] || ACCENTS.violet;
  const DirIcon = s.direction === "down" ? TrendingDown : TrendingUp;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4, delay: i * 0.06 }}
      data-testid={`painpoint-card-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={`group relative overflow-hidden rounded-2xl border bg-card p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md sm:p-5 ${tone.ring}`}
    >
      <div className={`pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full blur-3xl ${tone.glow}`} />

      <p className="relative text-[11px] font-bold uppercase leading-tight tracking-[0.16em] text-muted-foreground/85">
        {s.label}
      </p>

      <div className="relative mt-4 grid grid-cols-[1fr_auto_1fr] items-end gap-2">
        {/* Industry */}
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            Industry
          </p>
          <p className="mt-1 font-display text-[1.5rem] font-black leading-none tracking-[-0.02em] text-muted-foreground/70 line-through decoration-rose-400/70 sm:text-[1.875rem]">
            {s.industry}
          </p>
        </div>

        {/* Arrow */}
        <span className={`relative -mb-1.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-card ring-1 ring-inset ring-border ${tone.chip}`}>
          <DirIcon className="h-3.5 w-3.5" strokeWidth={3} />
        </span>

        {/* Ours */}
        <div className="text-right">
          <p className={`text-[10px] font-bold uppercase tracking-[0.18em] ${tone.chip}`}>
            Clinic OS
          </p>
          <p className={`mt-1 font-display text-[1.625rem] font-black leading-none tracking-[-0.025em] sm:text-[2rem] ${tone.chip}`}>
            {s.ours}
          </p>
        </div>
      </div>

      <p className="relative mt-4 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground/65">
        {s.sourceTag}
      </p>
    </motion.div>
  );
};

export const PainPoints = ({
  eyebrow = "Is this your clinic?",
  heading = "The numbers most clinics never see.",
  sub = "Industry averages on the left. What Clinic OS pilots actually do on the right. No magic — just every patient touch, owned end-to-end.",
  stats = DEFAULT_STATS,
  variant = "default",
  className = "",
  cta = null, // { label, onClick }
}) => {
  const wrap =
    variant === "inline"
      ? `relative ${className}`
      : `relative py-14 sm:py-20 ${className}`;

  return (
    <section data-testid="painpoints-section" className={wrap}>
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-rose-500/30 bg-rose-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-rose-700 dark:text-rose-300">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-rose-500" />
            </span>
            {eyebrow}
          </span>
          <h2 className="mt-4 font-display text-[1.625rem] font-black leading-[1.08] tracking-[-0.025em] sm:text-[2rem] lg:text-[2.5rem]">
            {heading}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-[13.5px] leading-relaxed text-muted-foreground sm:text-[15px]">
            {sub}
          </p>
        </div>

        <div className={`mt-8 grid grid-cols-1 gap-3 sm:mt-10 sm:gap-4 ${stats.length >= 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"}`}>
          {stats.map((s, i) => (
            <StatCard key={s.label} s={s} i={i} />
          ))}
        </div>

        {cta && (
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={cta.onClick}
              data-testid="painpoints-cta"
              className="group inline-flex h-11 items-center gap-2 rounded-xl bg-foreground px-5 text-[13.5px] font-bold text-background transition-all hover:-translate-y-0.5"
            >
              {cta.label}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default PainPoints;
